import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Shield, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { admin } = useAuth();

  if (!admin) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        {t('profile.title', { ns: 'common' })}
      </h1>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User size={28} className="text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                {admin.firstName} {admin.lastName}
              </h2>
              <p className="text-sm text-gray-500 capitalize">
                {admin.role.replace('_', ' ')}
              </p>
            </div>
          </div>

          <dl className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail size={20} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t('profile.email', { ns: 'common' })}
                </dt>
                <dd className="text-gray-900">{admin.email}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield size={20} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t('profile.role', { ns: 'common' })}
                </dt>
                <dd className="text-gray-900 capitalize">
                  {admin.role.replace('_', ' ')}
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar size={20} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t('profile.lastLogin', { ns: 'common' })}
                </dt>
                <dd className="text-gray-900">{formatDate(admin.lastLogin)}</dd>
              </div>
            </div>
          </dl>

          <p className="text-sm text-gray-500 pt-2 border-t border-gray-100">
            {t('profile.viewOnly', { ns: 'common' })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Profile;
